import { AccomOptionDto, AgeGroup } from '@app/model/deal.model';
import {
    MealPlanWithPrice,
    RoomOccupancyLimits,
    RoomWithCheapestMealPlan,
} from './hotel-booking.model';
import * as _ from 'lodash';
import { CalendarDayInfo } from '@app/controls/calendar/calendar.component';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {
    OccupancyLimits,
    OccupancySelection,
} from '@app/components/search-occupancy/search-occupancy.model';
import {
    addDaysToDateStruct,
    createDateString,
} from '@app/controls/calendar/calendar.utils';

export const processPrice = (price: number): string => {
    return `${(price / 1000).toFixed(1)}K`;
};

export const calculateCrossedPriceBasedOnPriceAndCrossedPercent = (
    price: number,
    crossedPercent: number
): number => {
    return price * (crossedPercent / 100 + 1);
};

export const getMealPlansGroupedByRoomId = (
    accomOptions: AccomOptionDto[],
    roomsWithCheapestMealPlans: RoomWithCheapestMealPlan[]
): Record<number, MealPlanWithPrice[]> => {
    const unorderedValues = accomOptions.reduce((acc, option) => {
        const {
            roomId,
            mealPlanType,
            mealPlanId,
            sellingPrice,
            crossedPrice,
            crossedPercent,
        } = option;
        const priceDifferenceWithCheapest =
            sellingPrice -
            roomsWithCheapestMealPlans.find((room) => room.roomId === roomId)
                .cheapestMealPlan.sellingPrice;

        // If the roomId key doesn't exist in the dictionary, initialize it
        if (!acc[roomId]) {
            acc[roomId] = [];
        }

        // Add the meal plan details to the roomId array
        acc[roomId].push({
            mealPlanType,
            mealPlanId,
            sellingPrice,
            crossedPrice,
            crossedPercent,
            priceDifferenceWithCheapest,
        });

        return acc;
    }, {} as Record<number, MealPlanWithPrice[]>);

    const sortedMealPlans = _.mapValues(unorderedValues, (mealPlans) =>
        mealPlans.sort((a, b) => a.sellingPrice - b.sellingPrice)
    );

    return sortedMealPlans;
};

export const computeCheapestMealPlans = (
    accomOptions: AccomOptionDto[]
): RoomWithCheapestMealPlan[] => {
    const roomMap = new Map<
        string,
        {
            mealPlanType: string;
            sellingPrice: number;
            roomId: number;
            crossedPrice: number;
            crossedPercent: number;
            occupancyLimits: RoomOccupancyLimits;
        }
    >();

    for (const option of accomOptions) {
        const {
            roomName,
            roomId,
            mealPlanType,
            sellingPrice,
            crossedPercent,
            crossedPrice,
            maxAdult,
            maxTeen,
            maxChild,
            maxInfant,
            maxGuest,
            maxPax,
        } = option;

        // If the roomName is not in the map or the current sellingPrice is cheaper, update the map
        if (
            !roomMap.has(roomName) ||
            roomMap.get(roomName)!.sellingPrice > sellingPrice
        ) {
            roomMap.set(roomName, {
                mealPlanType,
                sellingPrice,
                roomId,
                crossedPrice,
                crossedPercent,
                occupancyLimits: {
                    maxAdult,
                    maxTeen,
                    maxChild,
                    maxInfant,
                    maxGuestInclInfants: maxGuest,
                    maxPaxNoInfants: maxPax,
                },
            });
        }
    }

    // Convert the Map into an array of RoomWithCheapestMealPlan objects
    return Array.from(roomMap.entries())
        .map(([roomName, cheapestMealPlan]) => ({
            roomName,
            roomId: cheapestMealPlan.roomId,
            occupancyLimits: cheapestMealPlan.occupancyLimits,
            cheapestMealPlan,
        }))
        .sort(
            (a, b) =>
                a.cheapestMealPlan.sellingPrice -
                b.cheapestMealPlan.sellingPrice
        );
};

const areNextMlosDatesAvailable = (
    calendarDayInfo: CalendarDayInfo,
    additionalCalendarDayInfo: Map<string, CalendarDayInfo>
): boolean => {
    const { date, minLengthOfStay } = calendarDayInfo;
    for (let i = 1; i < minLengthOfStay; i++) {
        const nextDate = addDaysToDateStruct(date, i);
        if (!additionalCalendarDayInfo.has(createDateString(nextDate))) {
            return false;
        }
    }
    return true;
};

export const getFirstAvailableStartAndEndDate = (
    additionalCalendarDayInfo: Map<string, CalendarDayInfo>
): {
    startDate: NgbDate;
    endDate: NgbDate;
} => {
    const possibleStartDate = Array.from(
        additionalCalendarDayInfo.values()
    ).find((calendarDayInfo) => {
        const areDatesAvailable = areNextMlosDatesAvailable(
            calendarDayInfo,
            additionalCalendarDayInfo
        );
        if (areDatesAvailable) {
            return true;
        }
    });

    if (possibleStartDate) {
        const { date, minLengthOfStay } = possibleStartDate;
        const endDate = addDaysToDateStruct(date, minLengthOfStay);
        return {
            startDate: new NgbDate(date.year, date.month, date.day),
            endDate: endDate,
        };
    }

    return {
        startDate: null,
        endDate: null,
    };
};

export const setOccupancyLimits = (
    rooms: RoomWithCheapestMealPlan[],
    ageGroups: AgeGroup[]
): OccupancyLimits => {
    const laxestOccupancyLimits = rooms.reduce(
        (acc, room) => {
            const { occupancyLimits } = room;
            return {
                maxAdult: Math.max(acc.maxAdult, occupancyLimits.maxAdult),
                maxTeen: Math.max(acc.maxTeen, occupancyLimits.maxTeen),
                maxChild: Math.max(acc.maxChild, occupancyLimits.maxChild),
                maxInfant: Math.max(acc.maxInfant, occupancyLimits.maxInfant),
                maxGuestInclInfants: Math.max(
                    acc.maxGuestInclInfants,
                    occupancyLimits.maxGuestInclInfants
                ),
                maxPaxNoInfants: Math.max(
                    acc.maxPaxNoInfants,
                    occupancyLimits.maxPaxNoInfants
                ),
            };
        },
        {
            maxAdult: 0,
            maxTeen: 0,
            maxChild: 0,
            maxInfant: 0,
            maxGuestInclInfants: 0,
            maxPaxNoInfants: 0,
        }
    );

    // TODO: this relies on string literals. Not ideal
    const ageGroupsWithMaxPax = ageGroups.map((ageGroup) => {
        return {
            ...ageGroup,
            maxPax: laxestOccupancyLimits[`max${ageGroup.ageGroupType}`],
        };
    });

    const occupancyLimits = {
        ageGroups: ageGroupsWithMaxPax,
        maxGuestInclInfants: laxestOccupancyLimits.maxGuestInclInfants,
        maxPaxNoInfants: laxestOccupancyLimits.maxPaxNoInfants,
    };

    return occupancyLimits;
};

export const canOccupancySelectionFitInRoom = (
    occupancySelection: OccupancySelection,
    roomOccupancyLimits: RoomOccupancyLimits,
    ageGroups: AgeGroup[]
): boolean => {
    const allGuestCount =
        occupancySelection.adults + occupancySelection.children.length;
    if (allGuestCount > roomOccupancyLimits.maxGuestInclInfants) {
        return false;
    }
    const infantAgeGroup = ageGroups.find(
        (ageGroup) => ageGroup.ageGroupType === 'Infant'
    );
    const infantAgeBracket = infantAgeGroup
        ? [infantAgeGroup.ageFrom, infantAgeGroup.ageTo]
        : [0, 0];
    const allGuestNoInfantsCount =
        occupancySelection.adults +
        occupancySelection.children.filter((age) => age > infantAgeBracket[1])
            .length;
    if (allGuestNoInfantsCount > roomOccupancyLimits.maxPaxNoInfants) {
        return false;
    }

    const areThereTooManyInfants = infantAgeGroup
        ? occupancySelection.children.filter(
              (age) =>
                  age >= infantAgeGroup.ageFrom && age <= infantAgeGroup.ageTo
          ).length > roomOccupancyLimits.maxInfant
        : false;

    if (areThereTooManyInfants) {
        return false;
    }

    const childrenAgeGroup = ageGroups.find(
        (ageGroup) => ageGroup.ageGroupType === 'Child'
    );
    const areThereTooManyChildren = childrenAgeGroup
        ? occupancySelection.children.filter(
              (age) =>
                  age >= childrenAgeGroup.ageFrom &&
                  age <= childrenAgeGroup.ageTo
          ).length > roomOccupancyLimits.maxChild
        : false;
    if (areThereTooManyChildren) {
        return false;
    }

    const teensAgeGroup = ageGroups.find(
        (ageGroup) => ageGroup.ageGroupType === 'Teen'
    );
    const areThereTooManyTeens = teensAgeGroup
        ? occupancySelection.children.filter(
              (age) =>
                  age >= teensAgeGroup.ageFrom && age <= teensAgeGroup.ageTo
          ).length > roomOccupancyLimits.maxTeen
        : false;
    if (areThereTooManyTeens) {
        return false;
    }

    return true;
};
